import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, User, Save, Loader2, Play, Trash2, Camera } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { Pencil } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useComprehensiveNutrition } from '@/contexts/NutritionContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Analytics } from '@/lib/analytics';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export const Settings = () => {
  const navigate = useNavigate();
  const { profile, updateProfile } = useProfile();
  const { user, signOut } = useAuth();
  const { recalculateGoalsIfNeeded, nutritionGoals } = useComprehensiveNutrition();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDeleteAccount = async () => {
    if (!user) return;
    
    setIsDeleting(true);
    try {
      // Call edge function to delete user account (including from auth.users)
      const { data, error } = await supabase.functions.invoke('delete-user-account');

      if (error) {
        console.error('Error deleting account:', error);
        toast.error('Failed to delete account. Please try again.');
        return;
      }

      // Sign out the user locally
      await signOut();
      
      Analytics.accountDeleted();
      toast.success('Account deleted successfully');
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    fitness_level: '',
    primary_goals: [] as string[],
    experience_years: 0,
    height_cm: '',
    weight_kg: '',
    birth_date: '',
    gender: '',
    activity_level: '',
    ai_coach_name: 'Arnold'
  });

  // Update form data when profile loads or changes
  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        fitness_level: profile.fitness_level || '',
        primary_goals: profile.primary_goals || [],
        experience_years: profile.experience_years || 0,
        height_cm: profile.height_cm?.toString() || '',
        weight_kg: profile.weight_kg?.toString() || '',
        birth_date: profile.birth_date || '',
        gender: profile.gender || '',
        activity_level: profile.activity_level || '',
        ai_coach_name: profile.ai_coach_name || 'Arnold'
      });
    }
  }, [profile]);

  const fitnessLevels = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'elite', label: 'Elite' }
  ];

  const activityLevels = [
    { value: 'sedentary', label: 'Sedentary (little/no exercise)' },
    { value: 'light', label: 'Lightly active (1-3 days/week)' },
    { value: 'moderate', label: 'Moderately active (3-5 days/week)' },
    { value: 'active', label: 'Very active (6-7 days/week)' },
    { value: 'extreme', label: 'Extremely active (2x/day)' }
  ];

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const updates = {
        ...formData,
        height_cm: formData.height_cm ? parseInt(formData.height_cm) : null,
        weight_kg: formData.weight_kg ? parseInt(formData.weight_kg) : null,
      };

      const { error } = await updateProfile(updates);
      
      if (error) {
        toast.error('Failed to update profile');
        return;
      }

      Analytics.profileUpdated();
      Analytics.settingsChanged('profile');

      // Check if user has manual overrides - if so, don't auto-recalculate
      if (nutritionGoals?.is_manual_override) {
        toast.success('Profile updated! Your manual nutrition goals were preserved.');
      } else {
        // Recalculate nutrition goals based on updated profile
        try {
          await recalculateGoalsIfNeeded(true); // Force recalculation
          toast.success('Profile updated! Goals and nutrition targets refreshed.');
        } catch (goalError) {
          console.error('Error recalculating goals:', goalError);
          toast.success('Profile updated! Nutrition goals will refresh automatically.');
        }
      }

      navigate(-1); // Go back to previous page
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="bg-gradient-bg p-4 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-primary mb-2">Loading Profile...</h2>
          <p className="text-muted-foreground">Please wait while we load your profile information.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-bg p-4 pb-24">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-normal">PROFILE SETTINGS</h1>
            <p className="text-muted-foreground">Update your personal information and fitness preferences</p>
          </div>
        </div>

        {/* Profile Picture */}
        <Card className="bg-gradient-card border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-primary" />
              Profile Picture
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <div 
              className="relative w-24 h-24 rounded-full overflow-hidden cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}
            >
              {profile?.avatar_url ? (
                <img 
                  src={profile.avatar_url} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-primary flex items-center justify-center text-black font-bold text-2xl">
                  {profile?.first_name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {isUploadingAvatar ? (
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                ) : (
                  <Camera className="h-6 w-6 text-white" />
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || !user) return;
                
                setIsUploadingAvatar(true);
                try {
                  const fileExt = file.name.split('.').pop();
                  const filePath = `${user.id}/avatar.${fileExt}`;
                  
                  const { error: uploadError } = await supabase.storage
                    .from('avatars')
                    .upload(filePath, file, { upsert: true });
                  
                  if (uploadError) throw uploadError;
                  
                  const { data: { publicUrl } } = supabase.storage
                    .from('avatars')
                    .getPublicUrl(filePath);
                  
                  const avatarUrlWithCacheBust = `${publicUrl}?t=${Date.now()}`;
                  await updateProfile({ avatar_url: avatarUrlWithCacheBust });
                  toast.success('Profile picture updated!');
                } catch (error) {
                  console.error('Error uploading avatar:', error);
                  toast.error('Failed to upload profile picture.');
                } finally {
                  setIsUploadingAvatar(false);
                  e.target.value = '';
                }
              }}
            />
            <p className="text-xs text-muted-foreground">Tap to change your profile picture</p>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card className="bg-gradient-card border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="Enter your first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="birth_date">Birth Date</Label>
                <Input
                  id="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, birth_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <select
                  id="gender"
                  value={formData.gender}
                  onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="height_cm">Height (cm)</Label>
                <Input
                  id="height_cm"
                  type="number"
                  value={formData.height_cm}
                  onChange={(e) => setFormData(prev => ({ ...prev, height_cm: e.target.value }))}
                  placeholder="170"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight_kg">Weight (kg)</Label>
                <Input
                  id="weight_kg"
                  type="text"
                  inputMode="decimal"
                  value={formData.weight_kg}
                  onChange={(e) => {
                    const value = e.target.value.replace(',', '.');
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setFormData(prev => ({ ...prev, weight_kg: value }));
                    }
                  }}
                  placeholder="70"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fitness Information */}
        <Card className="bg-gradient-card border-border/50">
          <CardHeader>
            <CardTitle>Fitness Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fitness_level">Fitness Level</Label>
                <select
                  id="fitness_level"
                  value={formData.fitness_level}
                  onChange={(e) => setFormData(prev => ({ ...prev, fitness_level: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select fitness level</option>
                  {fitnessLevels.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="experience_years">Years of Experience</Label>
                <Input
                  id="experience_years"
                  type="number"
                  min="0"
                  max="50"
                  value={formData.experience_years}
                  onChange={(e) => setFormData(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="activity_level">Activity Level</Label>
              <select
                id="activity_level"
                value={formData.activity_level}
                onChange={(e) => setFormData(prev => ({ ...prev, activity_level: e.target.value }))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">Select activity level</option>
                {activityLevels.map(level => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* AI Coach Name */}
        <Card className="bg-gradient-card border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              AI Coach Name
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="ai_coach_name">Coach Name</Label>
              <Input
                id="ai_coach_name"
                value={formData.ai_coach_name}
                onChange={(e) => setFormData(prev => ({ ...prev, ai_coach_name: e.target.value }))}
                placeholder="Arnold"
              />
              <p className="text-xs text-muted-foreground">
                Change your AI coach's name. The coach will adopt this identity in all conversations.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex gap-3">
          <Button
            onClick={() => {
              try {
                // Immediately force tutorial to show again via localStorage + navigation
                localStorage.setItem('tutorial_completed', 'false');
                localStorage.setItem('force_tutorial', 'true');
                updateProfile({ tutorial_completed: false });
                toast.success('Tutorial restarted!');
                navigate('/dashboard', { replace: true });
              } catch (error) {
                console.error('Error resetting tutorial:', error);
                toast.error('Failed to restart tutorial');
              }
            }}
            variant="outline"
            className="flex-1 h-12"
          >
            <Play className="mr-2 h-4 w-4" />
            View Tutorial
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 h-12 bg-gradient-primary text-black shadow-glow hover:shadow-lg transition-all duration-300"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>

        {/* Delete Account */}
        <div className="pt-4 flex justify-center">
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10">
                <Trash2 className="h-3 w-3 mr-1" />
                Delete Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete your account
                  and remove all your data from our servers including:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Your profile information</li>
                    <li>All workout history and progress</li>
                    <li>Nutrition and meal logs</li>
                    <li>Goals and milestones</li>
                    <li>Chat history with your AI coach</li>
                  </ul>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Account'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};